import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	type EntityAnnotationsMessage,
	normalizeEntityAnnotationsMessage,
	type RawEntityAnnotationsMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';

const isRawEntityAnnotationsMessage = (value: unknown): value is RawEntityAnnotationsMessage =>
	typeof value === 'object' &&
	value !== null &&
	typeof (value as RawEntityAnnotationsMessage).sentence === 'string' &&
	Array.isArray((value as RawEntityAnnotationsMessage).entities);

const normalisePersistedBatch = (
	batch: RawEntityAnnotationsMessage,
): RawEntityAnnotationsMessage => ({
	...batch,
	entities: batch.entities.map((entity) => {
		// The persisted payload may use either casing; coalesce both.
		const raw = entity as Record<string, unknown>;
		return {
			...entity,
			canonical_name:
				typeof entity.canonical_name === 'string'
					? entity.canonical_name
					: typeof raw.canonicalName === 'string'
						? raw.canonicalName
						: entity.mention,
			confidence_score:
				typeof entity.confidence_score === 'number'
					? entity.confidence_score
					: typeof raw.confidenceScore === 'number'
						? raw.confidenceScore
						: 0,
		};
	}),
});

/**
 * Extracts persisted entity annotation batches from `message_metadata.entity_annotations`
 * and populates `storeAssistant.entityAnnotationBatches` so that hover cards render when
 * reloading chat history. Mirrors the streaming path that appends batches via
 * `appendEntityAnnotationBatch`.
 */
export const extractEntityAnnotationsFromMetadata = (
	storeAssistant: StoreAssistant,
	messageMetadata: Record<string, unknown> | undefined,
): void => {
	if (
		!messageMetadata ||
		!(fg('rovo_chat_inferred_project_hover_cards') || fg('rovo_chat_people_entity_hover_cards'))
	) {
		return;
	}

	const rawAnnotations = messageMetadata.entity_annotations;
	if (!Array.isArray(rawAnnotations) || rawAnnotations.length === 0) {
		return;
	}

	const validBatches: EntityAnnotationsMessage[] = [];
	for (const batch of rawAnnotations) {
		if (isRawEntityAnnotationsMessage(batch)) {
			validBatches.push(normalizeEntityAnnotationsMessage(normalisePersistedBatch(batch)));
		}
	}

	if (validBatches.length > 0) {
		storeAssistant.entityAnnotationBatches = validBatches;
	}
};
